import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { SnackbarService } from '../../../../../core/services';
import { ConfirmComponent } from '../../../../../components/core/confirm/confirm.component';
import { OrganizationGetCurrentRequest } from '../../../../../store/organization/organization.actions';
import { selectOrganization } from '../../../../../store/organization/organization.selectors';
import { UserGetAllRequest } from '../../../../../store/user/user.actions';
import { selectAllUsers, selectUser } from '../../../../../store/user/user.selectors';
import type { Organization } from '../../../../../store/organization/organization.models';
import { normalizeUserAttributes, type User } from '../../../../../store/user/user.models';
import { BRANDING } from '../../../../../core/config/branding';
import * as TaskActions from '../../../../../store/task/task.actions';
import { selectTaskDetail, selectTaskItems, selectTaskNotifications, selectTaskProjects, selectTaskSprints, selectTaskUnreadNotificationCount } from '../../../../../store/task/task.selectors';
import { TaskDetailsPanelComponent } from './task-details-panel/task-details-panel.component';
import type { TaskColumn, TaskItem, TaskNotification, TaskPriority, TaskProject, TaskScope, TaskSort, TaskSprint, TaskUpsertPayload, TaskViewMode } from '../../../../../store/task/task.models';
import { renderTaskDescription, summarizeTaskDescription } from './task-description.utils';
import { TaskRichTextEditorComponent } from './task-rich-text-editor/task-rich-text-editor.component';
import { UserHoverCardComponent } from '../user-hover-card/user-hover-card.component';

const TASK_STATUSES: ReadonlyArray<TaskColumn> = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'done', label: 'Done' },
];

const TASK_PRIORITIES: ReadonlyArray<{ value: TaskPriority; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const TASK_SCOPE_OPTIONS: ReadonlyArray<{ value: TaskScope; label: string }> = [
  { value: 'all', label: 'All tasks' },
  { value: 'assigned', label: 'My subtasks' },
  { value: 'created', label: 'Created by me' },
  { value: 'today', label: 'Due today' },
  { value: 'overdue', label: 'Overdue' },
];

const TASK_SORT_OPTIONS: ReadonlyArray<{ value: TaskSort; label: string }> = [
  { value: 'updated_desc', label: 'Recently updated' },
  { value: 'due_asc', label: 'Due date' },
  { value: 'priority_desc', label: 'Priority' },
  { value: 'title_asc', label: 'Title' },
];

const DEFAULT_TASK_SCOPE: TaskScope = 'all';
const DEFAULT_TASK_SORT: TaskSort = 'updated_desc';
const TASK_SAVED_FILTERS_KEY = 'meetrix.tasks.savedFilters.v1';
const ALL_SPRINTS_FILTER = '__all__';
const ACTIVE_SPRINT_FILTER = '__active__';
const NO_SPRINT_FILTER = '__none__';

type ReportCopyFormat = 'brief' | 'detailed';
type TaskReportTab = 'overview' | 'team' | 'planning' | 'time' | 'tags';

type TaskSavedFilter = {
  id: string;
  name: string;
  scope: TaskScope;
  sort: TaskSort;
  searchTerm: string;
  selectedTags: string[];
  selectedAssigneeIds: string[];
  userId: string;
  projectId: string | null;
  isFavorite: boolean;
  isDefault: boolean;
};

type TaskReportWorkloadRow = {
  id: string;
  name: string;
  total: number;
  inProgress: number;
  overdue: number;
  estimatedMinutes: number;
  trackedMinutes: number;
  utilization: number | null;
};

type TaskTimesheetRow = {
  id: string;
  name: string;
  totalMinutes: number;
  entryCount: number;
  runningCount: number;
  latestEntryAt: string | null;
};

type TaskUtilizationTimelineRow = {
  id: string;
  name: string;
  dailyMinutes: Array<{ label: string; minutes: number }>;
  totalMinutes: number;
};

type TaskReportCurrentWorkBucket = {
  id: string;
  name: string;
  tasks: TaskItem[];
  total: number;
  overdue: number;
  trackedMinutes: number;
};

type TaskReportPlannedBucket = {
  label: string;
  hint: string;
  tasks: TaskItem[];
};

type TaskReportSprintPlan = {
  label: string;
  hint: string;
  tasks: TaskItem[];
};

type TaskSubtaskTimeReportRow = {
  taskId: string;
  taskTitle: string;
  subtaskId: string;
  subtaskTitle: string;
  assigneeName: string;
  totalMinutes: number;
  contributors: Array<{ userId: string; name: string; minutes: number }>;
};

type TaskReportTabOption = {
  value: TaskReportTab;
  label: string;
  description: string;
};

type TaskReportOverviewHighlight = {
  label: string;
  value: string | number;
  hint: string;
  tone: 'default' | 'primary' | 'today' | 'success' | 'alert';
};

@Component({
  selector: 'app-tasks-screen',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    TaskDetailsPanelComponent,
    UserHoverCardComponent,
    MatCardModule,
    MatCheckboxModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltipModule,
  ],
  templateUrl: './tasks-screen.component.html',
  styleUrl: './tasks-screen.component.scss',
})
export class TasksScreenComponent {
  readonly notificationTarget = input<{ taskId: string; projectId: string } | null>(null);
  protected readonly brand = BRANDING;
  protected readonly projects = signal<TaskProject[]>([]);
  protected readonly sprints = signal<TaskSprint[]>([]);
  protected readonly tasks = signal<TaskItem[]>([]);
  protected readonly selectedProjectId = signal<string | null>(null);
  protected readonly selectedTaskId = signal<string | null>(null);
  protected readonly selectedTask = signal<TaskItem | null>(null);
  protected readonly showSearch = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly selectedScope = signal<TaskScope>(DEFAULT_TASK_SCOPE);
  protected readonly selectedSort = signal<TaskSort>(DEFAULT_TASK_SORT);
  protected readonly selectedView = signal<TaskViewMode>('board');
  protected readonly selectedSprintFilter = signal<string>(ALL_SPRINTS_FILTER);
  protected readonly selectedTagFilters = signal<string[]>([]);
  protected readonly selectedAssigneeFilters = signal<string[]>([]);
  protected readonly selectedReportTag = signal<string | null>(null);
  protected readonly selectedReportTab = signal<TaskReportTab>('overview');
  protected readonly utilizationRangeStart = signal('');
  protected readonly utilizationRangeEnd = signal('');
  protected readonly savedFilters = signal<TaskSavedFilter[]>([]);
  protected readonly taskNotifications = signal<TaskNotification[]>([]);
  protected readonly selectedTaskIds = signal<string[]>([]);
  protected readonly loading = signal(false);
  protected readonly currentUser = signal<User | null>(null);
  protected readonly organizationUsers = signal<User[]>([]);
  protected readonly organization = signal<Organization | null>(null);
  protected readonly defaultStatuses = TASK_STATUSES;
  protected readonly statuses = computed(() => {
    const projectStatuses = this.getSelectedProject()?.statuses ?? [];
    return projectStatuses.length ? projectStatuses : [...TASK_STATUSES];
  });
  protected readonly priorities = TASK_PRIORITIES;
  protected readonly scopeOptions = TASK_SCOPE_OPTIONS;
  protected readonly sortOptions = TASK_SORT_OPTIONS;
  protected readonly boardDropListIds = computed(() => this.statuses().map((status) => `tasks-status-${status.key}`));
  protected readonly filteredTasks = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();
    const currentUserId = this.currentUser()?.id ?? '';
    const now = new Date();
    const todayKey = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toDateString();
    let tasks = this.tasks().filter((task) => !task.is_archived);
    const selectedTags = new Set(this.selectedTagFilters().map((tag) => tag.trim().toLowerCase()).filter(Boolean));
    const selectedAssigneeIds = new Set(this.selectedAssigneeFilters().map((userId) => userId.trim()).filter(Boolean));

    const sprintFilter = this.selectedSprintFilter();
    if (sprintFilter !== ALL_SPRINTS_FILTER) {
      tasks = tasks.filter((task) => {
        if (sprintFilter === ACTIVE_SPRINT_FILTER) {
          return !!this.activeSprint()?.id && task.sprint_id === this.activeSprint()?.id;
        }
        if (sprintFilter === NO_SPRINT_FILTER) {
          return !task.sprint_id;
        }
        return (task.sprint_id ?? '') === sprintFilter;
      });
    }

    tasks = tasks.filter((task) => {
      switch (this.selectedScope()) {
        case 'assigned':
          return this.hasTaskAssignee(task, currentUserId);
        case 'created':
          return task.created_by === currentUserId;
        case 'today':
          return !!task.due_at && new Date(task.due_at).toDateString() === todayKey;
        case 'overdue':
          return !!task.due_at && new Date(task.due_at).getTime() < now.getTime();
        default:
          return true;
      }
    });

    if (selectedTags.size > 0) {
      tasks = tasks.filter((task) =>
        (task.tags ?? []).some((tag) => selectedTags.has(tag.trim().toLowerCase()))
      );
    }

    if (selectedAssigneeIds.size > 0) {
      tasks = tasks.filter((task) =>
        this.getTaskAssigneeIds(task).some((userId) => selectedAssigneeIds.has(userId))
      );
    }

    if (query) {
      tasks = tasks.filter((task) => {
        const assigneeName = this.getTaskAssigneeNames(task).toLowerCase();
        return (
          task.title.toLowerCase().includes(query) ||
          (task.description ?? '').toLowerCase().includes(query) ||
          assigneeName.includes(query) ||
          (task.tags ?? []).some((tag) => tag.toLowerCase().includes(query))
        );
      });
    }

    return [...tasks].sort((left, right) => {
      switch (this.selectedSort()) {
        case 'due_asc':
          return this.dateSortValue(left.due_at) - this.dateSortValue(right.due_at);
        case 'priority_desc':
          return this.priorityRank(right.priority) - this.priorityRank(left.priority);
        case 'title_asc':
          return left.title.localeCompare(right.title);
        default:
          return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
      }
    });
  });
  protected readonly canManageColumns = computed(() => this.canManageProject() && this.selectedView() === 'board');
  protected readonly activeScopeLabel = computed(() => this.scopeOptions.find((option) => option.value === this.selectedScope())?.label ?? 'All tasks');
  protected readonly selectedSprintFilterLabel = computed(() => {
    const sprintFilter = this.selectedSprintFilter();
    if (sprintFilter === ALL_SPRINTS_FILTER) {
      return 'All sprints';
    }
    if (sprintFilter === ACTIVE_SPRINT_FILTER) {
      return this.activeSprint()?.name ? `Active: ${this.activeSprint()!.name}` : 'Active sprint';
    }
    if (sprintFilter === NO_SPRINT_FILTER) {
      return 'No sprint';
    }
    return this.getSprintName(sprintFilter);
  });
  protected readonly hasActiveFilters = computed(() =>
    this.selectedScope() !== DEFAULT_TASK_SCOPE ||
    this.selectedSort() !== DEFAULT_TASK_SORT ||
    !!this.searchTerm().trim() ||
    this.selectedSprintFilter() !== ALL_SPRINTS_FILTER ||
    this.selectedTagFilters().length > 0 ||
    this.selectedAssigneeFilters().length > 0
  );
  protected readonly canSaveCurrentFilter = computed(() => this.hasActiveFilters() || !!this.searchTerm().trim());
  protected readonly filterTagOptions = computed(() => this.availableTaskTags());
  protected readonly filterAssigneeOptions = computed(() => {
    const assignedUserIds = new Set<string>();
    for (const task of this.projectTasks()) {
      for (const userId of this.getTaskAssigneeIds(task)) {
        assignedUserIds.add(userId);
      }
    }

    return this.getProjectUsers()
      .filter((user) => assignedUserIds.has(user.id))
      .sort((left, right) => left.name.localeCompare(right.name));
  });
  protected readonly activeTagFilterBadges = computed(() => this.selectedTagFilters().slice(0, 3));
  protected readonly activeTagFilterOverflowCount = computed(() => Math.max(this.selectedTagFilters().length - 3, 0));
  protected readonly activeAssigneeFilterBadges = computed(() =>
    this.selectedAssigneeFilters()
      .map((userId) => ({ id: userId, name: this.getUserName(userId) }))
      .filter((assignee) => assignee.name !== 'Unassigned')
      .slice(0, 3)
  );
  protected readonly activeAssigneeFilterOverflowCount = computed(() => Math.max(this.selectedAssigneeFilters().length - 3, 0));
  protected readonly unreadTaskNotificationCount = signal(0);
  protected readonly visibleSavedFilters = computed(() => {
    const currentUserId = this.currentUser()?.id ?? '';
    const currentProjectId = this.selectedProjectId();
    return this.savedFilters().filter((filter) =>
      filter.userId === currentUserId &&
      (filter.projectId === null || filter.projectId === currentProjectId),
    );
  });
  protected readonly favoriteSavedFilters = computed(() => this.visibleSavedFilters().filter((filter) => filter.isFavorite));
  protected readonly activeSprint = computed(() => this.sprints().find((sprint) => sprint.is_active && !sprint.is_archived) ?? null);
  protected readonly defaultSavedFilter = computed(() =>
    this.visibleSavedFilters().find((filter) => filter.isDefault) ?? null,
  );
  protected readonly selectedTasks = computed(() => {
    const selectedIds = new Set(this.selectedTaskIds());
    return this.projectTasks().filter((task) => selectedIds.has(task.id));
  });
  protected readonly hasBulkSelection = computed(() => this.selectedTasks().length > 0);
  protected readonly dashboardInsights = computed(() => {
    const tasks = this.tasks().filter((task) => !task.is_archived);
    const currentUserId = this.currentUser()?.id ?? '';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

    return [
      { label: 'Open tasks', value: tasks.filter((task) => task.status !== 'done').length, tone: 'default' },
      { label: 'Due today', value: tasks.filter((task) => !!task.due_at && new Date(task.due_at) >= startOfToday && new Date(task.due_at) < endOfToday).length, tone: 'today' },
      { label: 'Overdue', value: tasks.filter((task) => !!task.due_at && new Date(task.due_at) < startOfToday && task.status !== 'done').length, tone: 'overdue' },
      { label: 'My subtasks', value: tasks.filter((task) => this.hasTaskAssignee(task, currentUserId) && task.status !== 'done').length, tone: 'primary' },
      { label: 'Done this week', value: tasks.filter((task) => task.status === 'done' && new Date(task.updated_at) >= startOfWeek).length, tone: 'success' },
    ] as const;
  });
  protected readonly assigneeWorkload = computed(() => {
    const buckets = new Map<string, { id: string; name: string; total: number; overdue: number; dueToday: number }>();
    for (const task of this.projectTasks()) {
      for (const workItem of this.getOpenSubtaskWorkItems(task)) {
        const userId = workItem.ownerId;
        if (!userId) {
          continue;
        }
        const name = this.getUserName(userId);
        const existing = buckets.get(userId) ?? { id: userId, name, total: 0, overdue: 0, dueToday: 0 };
        existing.total += 1;
        const dueState = this.getDueState(task.due_at);
        if (dueState === 'overdue') {
          existing.overdue += 1;
        } else if (dueState === 'today') {
          existing.dueToday += 1;
        }
        buckets.set(userId, existing);
      }
    }
    return [...buckets.values()]
      .sort((left, right) => {
        if (right.overdue !== left.overdue) {
          return right.overdue - left.overdue;
        }
        if (right.total !== left.total) {
          return right.total - left.total;
        }
        return left.name.localeCompare(right.name);
      })
      .slice(0, 6);
  });
  protected readonly unassignedWorkload = computed(() => {
    const tasks = this.projectTasks().filter((task) => this.getOpenSubtaskWorkItems(task).some((workItem) => !workItem.ownerId));
    return {
      total: tasks.length,
      overdue: tasks.filter((task) => this.getDueState(task.due_at) === 'overdue').length,
      dueToday: tasks.filter((task) => this.getDueState(task.due_at) === 'today').length,
    };
  });
  protected readonly reportTasks = computed(() => this.projectTasks());
  protected readonly assignedReportTasks = computed(() => {
    const currentUserId = this.currentUser()?.id ?? '';
    return this.reportTasks().filter((task) => this.hasAssignedSubtask(task, currentUserId));
  });
  protected readonly reportTabs = computed<ReadonlyArray<TaskReportTabOption>>(() => this.canManageProject()
    ? [
      { value: 'overview', label: 'Overview', description: 'Project health, current work, and key signals.' },
      { value: 'team', label: 'Team', description: 'Who is carrying work and how effort is distributed.' },
      { value: 'planning', label: 'Planning', description: 'Upcoming work and sprint organization.' },
      { value: 'time', label: 'Time', description: 'Tracked effort, timesheets, and time breakdowns.' },
      { value: 'tags', label: 'Tags', description: 'Inspect workload and risk through task tags.' },
    ]
    : [
      { value: 'overview', label: 'Overview', description: 'Your current work, upcoming tasks, and priorities.' },
      { value: 'time', label: 'Time', description: 'Your logged time and subtask effort breakdowns.' },
      { value: 'tags', label: 'Tags', description: 'Focus areas and workload slices by tag.' },
    ]);
  protected readonly activeReportTab = computed<TaskReportTab>(() => {
    const selected = this.selectedReportTab();
    return this.reportTabs().some((tab) => tab.value === selected)
      ? selected
      : (this.reportTabs()[0]?.value ?? 'overview');
  });
  protected readonly reportTabIndex = computed(() => {
    const activeTab = this.activeReportTab();
    return Math.max(this.reportTabs().findIndex((tab) => tab.value === activeTab), 0);
  });
  protected readonly reportAudienceTitle = computed(() => this.canManageProject() ? 'Project reporting workspace' : 'My reporting workspace');
  protected readonly reportAudienceDescription = computed(() => this.canManageProject()
    ? 'Switch between overview, team, planning, time, and tag views so reporting stays organized as projects grow.'
    : 'Review your own workload, logged effort, and tagged work without the team-wide reporting clutter.');
  protected readonly reportTagSummary = computed(() => {
    const counts = new Map<string, number>();
    for (const task of this.reportTasks()) {
      for (const tag of task.tags ?? []) {
        const normalizedTag = tag.trim();
        if (!normalizedTag) {
          continue;
        }
        counts.set(normalizedTag, (counts.get(normalizedTag) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((left, right) => right.count - left.count || left.tag.localeCompare(right.tag));
  });
  protected readonly availableTaskTags = computed(() => {
    const merged = new Set<string>();

    for (const tag of this.organization()?.config?.saved_task_tags ?? []) {
      const normalized = tag.trim().toLowerCase();
      if (normalized) {
        merged.add(normalized);
      }
    }

    for (const row of this.reportTagSummary()) {
      const normalized = row.tag.trim().toLowerCase();
      if (normalized) {
        merged.add(normalized);
      }
    }

    return [...merged].sort((left, right) => left.localeCompare(right));
  });
  protected readonly taggedReportTasks = computed(() => {
    const selectedTag = this.selectedReportTag();
    if (!selectedTag) {
      return this.reportTasks();
    }
    return this.reportTasks().filter((task) => (task.tags ?? []).includes(selectedTag));
  });
  protected readonly currentWorkReport = computed<TaskReportCurrentWorkBucket[]>(() => {
    const buckets = new Map<string, TaskReportCurrentWorkBucket>();
    const bucketTaskKeys = new Map<string, Set<string>>();
    const activeTasks = this.reportTasks()
      .filter((task) => task.status === 'in_progress')
      .sort((left, right) => this.dateSortValue(left.due_at) - this.dateSortValue(right.due_at));

    for (const task of activeTasks) {
      for (const workItem of this.getOpenSubtaskWorkItems(task)) {
        const id = workItem.ownerId;
        const key = id || '';
        const name = this.getUserName(id || null);
        const bucket = buckets.get(key) ?? {
          id: key,
          name,
          tasks: [],
          total: 0,
          overdue: 0,
          trackedMinutes: 0,
        };

        const taskKeySet = bucketTaskKeys.get(key) ?? new Set<string>();
        if (!taskKeySet.has(task.id)) {
          taskKeySet.add(task.id);
          bucket.tasks.push(task);
          bucketTaskKeys.set(key, taskKeySet);
        }

        bucket.total += 1;
        bucket.trackedMinutes += this.getTrackedMinutesForRange(task, null, null, workItem.subtaskId, id);
        if (this.getDueState(task.due_at) === 'overdue') {
          bucket.overdue += 1;
        }
        buckets.set(key, bucket);
      }
    }

    return [...buckets.values()].sort((left, right) => {
      if (right.overdue !== left.overdue) {
        return right.overdue - left.overdue;
      }
      if (right.total !== left.total) {
        return right.total - left.total;
      }
      return left.name.localeCompare(right.name);
    });
  });
  protected readonly plannedTaskBuckets = computed<TaskReportPlannedBucket[]>(() => {
    const plannedTasks = this.reportTasks().filter((task) => task.status === 'backlog' || task.status === 'todo');
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const nextWeek = new Date(startOfToday);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const fortnight = new Date(startOfToday);
    fortnight.setDate(fortnight.getDate() + 14);

    return [
      {
        label: 'Ready this week',
        hint: 'Planned tasks due in the next 7 days.',
        tasks: plannedTasks.filter((task) => !!task.due_at && new Date(task.due_at) >= startOfToday && new Date(task.due_at) < nextWeek),
      },
      {
        label: 'Next up',
        hint: 'Planned work due in the following week.',
        tasks: plannedTasks.filter((task) => !!task.due_at && new Date(task.due_at) >= nextWeek && new Date(task.due_at) < fortnight),
      },
      {
        label: 'Needs scheduling',
        hint: 'Backlog items without a concrete date.',
        tasks: plannedTasks.filter((task) => !task.due_at),
      },
    ];
  });
  protected readonly sprintPlans = computed<TaskReportSprintPlan[]>(() => {
    const assignedSprintPlans = this.sprints()
      .filter((sprint) => !sprint.is_archived)
      .map((sprint) => ({
        label: sprint.name,
        hint: sprint.goal?.trim() || (sprint.is_active ? 'Active sprint' : 'Planned sprint'),
        tasks: this.reportTasks().filter((task) => (task.sprint_id ?? null) === sprint.id),
      }));

    if (assignedSprintPlans.length) {
      const unscheduled = this.reportTasks().filter((task) => task.status !== 'done' && !task.sprint_id);
      return [
        ...assignedSprintPlans,
        {
          label: 'Unscheduled work',
          hint: 'Open tasks not yet committed to a sprint.',
          tasks: unscheduled.slice(0, 8),
        },
      ];
    }

    const candidates = this.reportTasks()
      .filter((task) => task.status !== 'done')
      .sort((left, right) => {
        const priorityDelta = this.priorityRank(right.priority) - this.priorityRank(left.priority);
        if (priorityDelta !== 0) {
          return priorityDelta;
        }
        return this.dateSortValue(left.due_at) - this.dateSortValue(right.due_at);
      });

    const currentSprint = candidates.filter((task) =>
      task.status === 'in_progress' ||
      task.priority === 'urgent' ||
      task.priority === 'high' ||
      this.isWithinDays(task.due_at, 14),
    ).slice(0, 8);
    const currentSprintIds = new Set(currentSprint.map((task) => task.id));
    const nextSprint = candidates.filter((task) => !currentSprintIds.has(task.id)).slice(0, 8);

    return [
      {
        label: 'Current sprint proposal',
        hint: 'High priority and near-term work to keep active this sprint.',
        tasks: currentSprint,
      },
      {
        label: 'Future sprint pool',
        hint: 'Candidates to bring into the next planning cycle.',
        tasks: nextSprint,
      },
    ];
  });
  protected readonly utilizationReport = computed<TaskReportWorkloadRow[]>(() => {
    const users = this.getProjectUsers();
    const rows = new Map<string, TaskReportWorkloadRow>();
    const rangeStart = this.parseDateRangeBoundary(this.utilizationRangeStart(), 'start');
    const rangeEnd = this.parseDateRangeBoundary(this.utilizationRangeEnd(), 'end');

    for (const user of users) {
      rows.set(user.id, {
        id: user.id,
        name: user.name,
        total: 0,
        inProgress: 0,
        overdue: 0,
        estimatedMinutes: 0,
        trackedMinutes: 0,
        utilization: null,
      });
    }

    for (const task of this.reportTasks()) {
      const workItems = this.getOpenSubtaskWorkItems(task);
      const estimatedShare = workItems.length > 0 ? Math.round((task.estimated_minutes ?? 0) / workItems.length) : 0;
      for (const workItem of workItems) {
        const userId = workItem.ownerId;
        const key = userId || '';
        const name = this.getUserName(userId || null);
        const row = rows.get(key) ?? {
          id: key,
          name,
          total: 0,
          inProgress: 0,
          overdue: 0,
          estimatedMinutes: 0,
          trackedMinutes: 0,
          utilization: null,
        };
        row.total += task.status === 'done' ? 0 : 1;
        row.inProgress += task.status === 'in_progress' ? 1 : 0;
        row.overdue += this.getDueState(task.due_at) === 'overdue' && task.status !== 'done' ? 1 : 0;
        row.estimatedMinutes += estimatedShare;
        row.trackedMinutes += this.getTrackedMinutesForRange(task, rangeStart, rangeEnd, workItem.subtaskId, userId);
        rows.set(key, row);
      }
    }

    return [...rows.values()]
      .map((row) => ({
        ...row,
        utilization: row.estimatedMinutes > 0 ? Math.min(999, Math.round((row.trackedMinutes / row.estimatedMinutes) * 100)) : null,
      }))
      .sort((left, right) => {
        if (right.overdue !== left.overdue) {
          return right.overdue - left.overdue;
        }
        if (right.total !== left.total) {
          return right.total - left.total;
        }
        return left.name.localeCompare(right.name);
      });
  });
  protected readonly userTimesheets = computed<TaskTimesheetRow[]>(() => {
    const rows = new Map<string, TaskTimesheetRow>();
    const rangeStart = this.parseDateRangeBoundary(this.utilizationRangeStart(), 'start');
    const rangeEnd = this.parseDateRangeBoundary(this.utilizationRangeEnd(), 'end');

    for (const task of this.reportTasks()) {
      for (const entry of task.time_entries ?? []) {
        const endedAt = entry.ended_at ? new Date(entry.ended_at) : new Date();
        if (Number.isNaN(endedAt.getTime())) {
          continue;
        }
        if (rangeStart && endedAt < rangeStart) {
          continue;
        }
        if (rangeEnd) {
          const startedAt = entry.started_at ? new Date(entry.started_at) : endedAt;
          if (startedAt > rangeEnd) {
            continue;
          }
        }

        const userId = entry.created_by ?? '';
        const row = rows.get(userId) ?? {
          id: userId,
          name: this.getUserName(userId),
          totalMinutes: 0,
          entryCount: 0,
          runningCount: 0,
          latestEntryAt: null,
        };
        row.totalMinutes += entry.minutes ?? 0;
        row.entryCount += 1;
        row.runningCount += entry.ended_at ? 0 : 1;
        row.latestEntryAt = !row.latestEntryAt || endedAt.toISOString() > row.latestEntryAt ? endedAt.toISOString() : row.latestEntryAt;
        rows.set(userId, row);
      }
    }

    return [...rows.values()].sort((left, right) => right.totalMinutes - left.totalMinutes || left.name.localeCompare(right.name));
  });
  protected readonly utilizationTimeline = computed<TaskUtilizationTimelineRow[]>(() => {
    const now = new Date();
    const rangeEnd = this.parseDateRangeBoundary(this.utilizationRangeEnd(), 'end') ?? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const rangeStart = this.parseDateRangeBoundary(this.utilizationRangeStart(), 'start') ?? new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate() - 6, 0, 0, 0, 0);
    const dayLabels = this.buildDateRangeLabels(rangeStart, rangeEnd);
    const rows = new Map<string, TaskUtilizationTimelineRow>();

    for (const task of this.reportTasks()) {
      for (const entry of task.time_entries ?? []) {
        const endedAt = entry.ended_at ? new Date(entry.ended_at) : new Date();
        if (Number.isNaN(endedAt.getTime()) || endedAt < rangeStart || endedAt > rangeEnd) {
          continue;
        }
        const key = endedAt.toISOString().slice(0, 10);
        const userId = entry.created_by ?? '';
        const row = rows.get(userId) ?? {
          id: userId,
          name: this.getUserName(userId),
          dailyMinutes: dayLabels.map((label) => ({ label, minutes: 0 })),
          totalMinutes: 0,
        };
        const bucket = row.dailyMinutes.find((item) => item.label === key);
        if (bucket) {
          bucket.minutes += entry.minutes ?? 0;
        }
        row.totalMinutes += entry.minutes ?? 0;
        rows.set(userId, row);
      }
    }

    return [...rows.values()].sort((left, right) => right.totalMinutes - left.totalMinutes || left.name.localeCompare(right.name));
  });
  protected readonly subtaskTimeReport = computed<TaskSubtaskTimeReportRow[]>(() => {
    const rangeStart = this.parseDateRangeBoundary(this.utilizationRangeStart(), 'start');
    const rangeEnd = this.parseDateRangeBoundary(this.utilizationRangeEnd(), 'end');
    const rows: TaskSubtaskTimeReportRow[] = [];

    for (const task of this.reportTasks()) {
      for (const subtask of task.subtasks ?? []) {
        const contributorMap = new Map<string, { userId: string; name: string; minutes: number }>();

        for (const entry of task.time_entries ?? []) {
          if (!entry.ended_at || (entry.subtask_id ?? '') !== subtask.id) {
            continue;
          }

          const endedAt = new Date(entry.ended_at);
          if (Number.isNaN(endedAt.getTime())) {
            continue;
          }
          if (rangeStart && endedAt < rangeStart) {
            continue;
          }
          if (rangeEnd) {
            const startedAt = entry.started_at ? new Date(entry.started_at) : endedAt;
            if (startedAt > rangeEnd) {
              continue;
            }
          }

          const userId = (entry.created_by ?? '').trim();
          const key = userId || '';
          const contributor = contributorMap.get(key) ?? {
            userId: key,
            name: this.getUserName(userId || null),
            minutes: 0,
          };
          contributor.minutes += entry.minutes ?? 0;
          contributorMap.set(key, contributor);
        }

        const contributors = [...contributorMap.values()]
          .sort((left, right) => right.minutes - left.minutes || left.name.localeCompare(right.name));
        const totalMinutes = contributors.reduce((sum, contributor) => sum + contributor.minutes, 0);
        if (!totalMinutes) {
          continue;
        }

        rows.push({
          taskId: task.id,
          taskTitle: task.title,
          subtaskId: subtask.id,
          subtaskTitle: subtask.title,
          assigneeName: this.getUserName(subtask.assignee_user_id ?? null),
          totalMinutes,
          contributors,
        });
      }
    }

    return rows.sort((left, right) => right.totalMinutes - left.totalMinutes || left.taskTitle.localeCompare(right.taskTitle) || left.subtaskTitle.localeCompare(right.subtaskTitle));
  });
  protected readonly reportSummaryCards = computed(() => {
    const tasks = this.reportTasks();
    const plannedTasks = tasks.filter((task) => task.status === 'backlog' || task.status === 'todo');
    const activeTasks = tasks.filter((task) => task.status === 'in_progress');
    const tagCount = this.reportTagSummary().length;

    return [
      { label: 'Current work', value: activeTasks.length, hint: 'Tasks actively being worked on', tone: 'primary' },
      { label: 'Planned tasks', value: plannedTasks.length, hint: 'Backlog and to-do items ahead', tone: 'default' },
      { label: this.activeSprint() ? 'Active sprint' : 'Sprint candidates', value: this.activeSprint() ? this.reportTasks().filter((task) => task.sprint_id === this.activeSprint()!.id).length : (this.sprintPlans()[0]?.tasks.length ?? 0), hint: this.activeSprint() ? this.activeSprint()!.name : 'Suggested current sprint load', tone: 'today' },
      { label: 'Tracked effort', value: this.formatMinutes(tasks.reduce((sum, task) => sum + (task.tracked_minutes ?? 0), 0)), hint: 'Tracked time across visible tasks', tone: 'success' },
      { label: 'Tracked subtasks', value: this.subtaskTimeReport().length, hint: 'Subtasks with logged time in the selected range', tone: 'default' },
      { label: 'Sprint count', value: this.sprints().length, hint: 'Configured project sprints', tone: 'default' },
      { label: 'Tag coverage', value: tagCount, hint: 'Distinct tags available for reporting', tone: 'default' },
    ] as const;
  });
  protected readonly adminOverviewHighlights = computed<TaskReportOverviewHighlight[]>(() => {
    const tasks = this.reportTasks();
    const openTasks = tasks.filter((task) => task.status !== 'done');
    const dueThisWeek = openTasks.filter((task) => {
      const dueState = this.getDueState(task.due_at);
      return dueState === 'today' || this.isWithinDays(task.due_at, 7);
    });
    const trackedMinutes = tasks.reduce((sum, task) => sum + (task.tracked_minutes ?? 0), 0);

    return [
      { label: 'Open tasks', value: openTasks.length, hint: 'Active project workload across all statuses except done.', tone: 'primary' },
      { label: 'Overdue tasks', value: openTasks.filter((task) => this.getDueState(task.due_at) === 'overdue').length, hint: 'Work that is already behind schedule.', tone: 'alert' },
      { label: 'Due this week', value: dueThisWeek.length, hint: 'Open tasks landing in the next 7 days.', tone: 'today' },
      { label: 'Unassigned work', value: this.unassignedWorkload().total, hint: 'Open tasks with at least one subtask missing an owner.', tone: 'default' },
      { label: 'Tracked effort', value: this.formatMinutes(trackedMinutes), hint: 'Logged time across visible project tasks.', tone: 'success' },
    ];
  });
  protected readonly personalReportSummaryCards = computed(() => {
    const currentUserId = this.currentUser()?.id ?? '';
    const tasks = this.assignedReportTasks();
    const openTasks = tasks.filter((task) => task.status !== 'done');
    const activeTasks = openTasks.filter((task) => task.status === 'in_progress');
    const overdueTasks = openTasks.filter((task) => this.getDueState(task.due_at) === 'overdue');
    const dueSoonTasks = openTasks.filter((task) => {
      const dueState = this.getDueState(task.due_at);
      return dueState === 'today' || this.isWithinDays(task.due_at, 7);
    });
    const trackedMinutes = tasks.reduce((sum, task) => sum + this.getTrackedMinutesForRange(task, null, null, null, currentUserId), 0);
    const tagCount = new Set(tasks.flatMap((task) => task.tags ?? []).map((tag) => tag.trim()).filter(Boolean)).size;

    return [
      { label: 'My open work', value: openTasks.length, hint: 'Tasks with subtasks assigned to you', tone: 'primary' },
      { label: 'In progress', value: activeTasks.length, hint: 'Assigned tasks currently moving', tone: 'today' },
      { label: 'Overdue', value: overdueTasks.length, hint: 'Assigned work past due', tone: 'default' },
      { label: 'Due soon', value: dueSoonTasks.length, hint: 'Assigned work due within the next week', tone: 'success' },
      { label: 'Tracked effort', value: this.formatMinutes(trackedMinutes), hint: 'Time you have logged across visible tasks', tone: 'default' },
      { label: 'Tag coverage', value: tagCount, hint: 'Distinct tags across your assigned work', tone: 'default' },
    ] as const;
  });
  protected readonly personalOverviewHighlights = computed<TaskReportOverviewHighlight[]>(() => {
    const currentUserId = this.currentUser()?.id ?? '';
    const tasks = this.assignedReportTasks();
    const openTasks = tasks.filter((task) => task.status !== 'done');
    const activeTasks = openTasks.filter((task) => task.status === 'in_progress');
    const dueSoonTasks = openTasks.filter((task) => {
      const dueState = this.getDueState(task.due_at);
      return dueState === 'today' || this.isWithinDays(task.due_at, 7);
    });
    const trackedMinutes = tasks.reduce((sum, task) => sum + this.getTrackedMinutesForRange(task, null, null, null, currentUserId), 0);

    return [
      { label: 'My open work', value: openTasks.length, hint: 'Tasks with subtasks currently assigned to you.', tone: 'primary' },
      { label: 'In progress', value: activeTasks.length, hint: 'Assigned tasks actively moving forward.', tone: 'today' },
      { label: 'Needs attention', value: openTasks.filter((task) => this.getDueState(task.due_at) === 'overdue').length, hint: 'Assigned work already past its due date.', tone: 'alert' },
      { label: 'Due soon', value: dueSoonTasks.length, hint: 'Assigned work due within the next week.', tone: 'default' },
      { label: 'Tracked effort', value: this.formatMinutes(trackedMinutes), hint: 'Time you have logged across your visible work.', tone: 'success' },
    ];
  });
  protected readonly overviewHighlights = computed(() => this.canManageProject()
    ? this.adminOverviewHighlights()
    : this.personalOverviewHighlights());
  protected readonly adminOverviewRiskTasks = computed(() => this.reportTasks()
    .filter((task) =>
      task.status !== 'done' && (
        this.getDueState(task.due_at) === 'overdue' ||
        task.priority === 'urgent' ||
        task.priority === 'high' ||
        this.getTaskAssigneeIds(task).length === 0
      ))
    .sort((left, right) => {
      const overdueDelta = Number(this.getDueState(right.due_at) === 'overdue') - Number(this.getDueState(left.due_at) === 'overdue');
      if (overdueDelta !== 0) {
        return overdueDelta;
      }
      const priorityDelta = this.priorityRank(right.priority) - this.priorityRank(left.priority);
      if (priorityDelta !== 0) {
        return priorityDelta;
      }
      return this.dateSortValue(left.due_at) - this.dateSortValue(right.due_at);
    })
    .slice(0, 6));
  protected readonly adminOverviewPlanningTasks = computed(() => this.reportTasks()
    .filter((task) => task.status !== 'done' && this.getDueState(task.due_at) !== 'overdue')
    .sort((left, right) => this.dateSortValue(left.due_at) - this.dateSortValue(right.due_at))
    .slice(0, 6));
  protected readonly personalOverviewAttentionTasks = computed(() => this.assignedReportTasks()
    .filter((task) => task.status !== 'done' && this.getDueState(task.due_at) !== 'none')
    .sort((left, right) => {
      const overdueDelta = Number(this.getDueState(right.due_at) === 'overdue') - Number(this.getDueState(left.due_at) === 'overdue');
      if (overdueDelta !== 0) {
        return overdueDelta;
      }
      return this.dateSortValue(left.due_at) - this.dateSortValue(right.due_at);
    })
    .slice(0, 6));
  protected readonly personalOverviewCurrentTasks = computed(() => this.assignedReportTasks()
    .filter((task) => task.status !== 'done')
    .sort((left, right) => {
      const statusDelta = Number(right.status === 'in_progress') - Number(left.status === 'in_progress');
      if (statusDelta !== 0) {
        return statusDelta;
      }
      return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
    })
    .slice(0, 6));
  protected readonly activeReportSummaryCards = computed(() => this.canManageProject()
    ? this.reportSummaryCards()
    : this.personalReportSummaryCards());

  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(SnackbarService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private lastHandledNotificationTargetKey: string | null = null;
  constructor() {
    this.store.select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.currentUser.set(user);
        if (user) {
          this.store.dispatch(OrganizationGetCurrentRequest());
          return;
        }

        this.organization.set(null);
      });

    this.store.select(selectOrganization)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((organization) => this.organization.set(organization));

    this.store.select(selectAllUsers)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => {
        const list = Array.isArray(users) ? users as User[] : [];
        this.organizationUsers.set(list);
      });

    this.store.select(selectTaskProjects)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projects) => {
        const nextProjects = Array.isArray(projects) ? projects : [];
        this.projects.set(nextProjects);

        const currentSelection = this.selectedProjectId();
        const nextSelection = nextProjects.some((project) => project.id === currentSelection)
          ? currentSelection
          : (nextProjects[0]?.id ?? null);

        if (nextSelection !== currentSelection) {
          this.selectedProjectId.set(nextSelection);
          if (nextSelection) {
            this.store.dispatch(TaskActions.TaskSprintsFetchRequest({ projectId: nextSelection }));
            this.store.dispatch(TaskActions.TaskItemsFetchRequest({ projectId: nextSelection }));
            queueMicrotask(() => this.applyDefaultSavedFilter());
          } else {
            this.sprints.set([]);
            this.tasks.set([]);
            this.closeTaskDetails();
          }
        }
      });

    this.store.select(selectTaskSprints)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sprints) => {
        this.sprints.set(Array.isArray(sprints) ? sprints : []);
      });

    this.store.select(selectTaskItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tasks) => {
        const nextTasks = Array.isArray(tasks) ? tasks : [];
        this.tasks.set(nextTasks);
        this.selectedTaskIds.update((selectedIds) => selectedIds.filter((selectedId) => nextTasks.some((task) => task.id === selectedId)));
        const selectedTaskId = this.selectedTaskId();
        if (!selectedTaskId) {
          return;
        }
        const fallbackTask = nextTasks.find((task) => task.id === selectedTaskId) ?? null;
        if (!this.selectedTask() || this.selectedTask()?.id === selectedTaskId) {
          this.selectedTask.set(fallbackTask);
        }
      });

    this.store.select(selectTaskNotifications)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notifications) => {
        this.taskNotifications.set(Array.isArray(notifications) ? notifications : []);
      });

    this.store.select(selectTaskUnreadNotificationCount)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.unreadTaskNotificationCount.set(count ?? 0));

    this.store.select(selectTaskDetail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((task) => {
        if (!task) {
          return;
        }
        this.selectedTask.set(task);
      });

    this.actions$
      .pipe(
        ofType(TaskActions.TaskExternalChangeReceived),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ projectId, taskId }) => {
        if (projectId !== this.selectedProjectId()) {
          return;
        }
        this.store.dispatch(TaskActions.TaskItemsFetchRequest({ projectId }));
        if (this.selectedTaskId() === taskId) {
          this.store.dispatch(TaskActions.TaskDetailFetchRequest({ taskId }));
        }
      });

    effect(() => {
      const target = this.notificationTarget();
      if (!target?.taskId || !target.projectId) {
        this.lastHandledNotificationTargetKey = null;
        return;
      }

      const targetKey = `${target.projectId}:${target.taskId}`;
      if (this.lastHandledNotificationTargetKey === targetKey) {
        return;
      }

      if (this.selectedProjectId() !== target.projectId) {
        this.onProjectChange(target.projectId);
        return;
      }

      const task = this.tasks().find((item) => item.id === target.taskId);
      if (task) {
        this.lastHandledNotificationTargetKey = targetKey;
        this.openTaskDetails(task);
        return;
      }

      this.lastHandledNotificationTargetKey = targetKey;
      this.selectedTaskId.set(target.taskId);
      this.store.dispatch(TaskActions.TaskDetailFetchRequest({ taskId: target.taskId }));
    });

    effect(() => {
      const activeTab = this.activeReportTab();
      if (this.selectedReportTab() !== activeTab) {
        this.selectedReportTab.set(activeTab);
      }
    });
  }

  ngOnInit(): void {
    this.loadSavedFilters();
    this.store.dispatch(TaskActions.TaskProjectsFetchRequest());
  }

  protected onViewChange(view: TaskViewMode): void {
    this.selectedView.set(view);
    if (view === 'reports') {
      this.clearTaskSelection();
    }
  }

  protected onReportTabChange(index: number): void {
    const tab = this.reportTabs()[index];
    if (!tab) {
      return;
    }
    this.selectedReportTab.set(tab.value);
  }

  protected onScopeChange(scope: TaskScope): void {
    this.selectedScope.set(scope);
  }

  protected toggleSearch(): void {
    const next = !this.showSearch();
    this.showSearch.set(next);
    if (!next) {
      this.searchTerm.set('');
    }
  }

  protected onSortChange(sort: TaskSort): void {
    this.selectedSort.set(sort);
  }

  protected clearFilters(): void {
    this.selectedScope.set(DEFAULT_TASK_SCOPE);
    this.selectedSort.set(DEFAULT_TASK_SORT);
    this.selectedSprintFilter.set(ALL_SPRINTS_FILTER);
    this.selectedTagFilters.set([]);
    this.selectedAssigneeFilters.set([]);
    this.searchTerm.set('');
    this.selectedReportTag.set(null);
  }

  protected onSprintFilterChange(value: string): void {
    this.selectedSprintFilter.set(value);
  }

  protected onTagFilterChange(value: string[] | null | undefined): void {
    this.selectedTagFilters.set(
      [...new Set((value ?? []).map((tag) => tag.trim().toLowerCase()).filter(Boolean))].sort((left, right) => left.localeCompare(right))
    );
  }

  protected onAssigneeFilterChange(value: string[] | null | undefined): void {
    this.selectedAssigneeFilters.set(
      [...new Set((value ?? []).map((userId) => userId.trim()).filter(Boolean))]
    );
  }

  protected toggleReportTag(tag: string): void {
    this.selectedReportTag.update((current) => current === tag ? null : tag);
  }

  protected copyCurrentWorkReport(): void {
    const sections = this.currentWorkReport().map((bucket) => ({
      label: bucket.name,
      tasks: bucket.tasks,
    }));
    this.copyTaskSectionsToClipboard('Current work report', sections, 'brief');
  }

  protected copyPlannedTaskReport(): void {
    const sections = this.plannedTaskBuckets().map((bucket) => ({
      label: bucket.label,
      tasks: bucket.tasks,
    }));
    this.copyTaskSectionsToClipboard('Planned tasks report', sections, 'brief');
  }

  protected copySprintPlanReport(): void {
    const sections = this.sprintPlans().map((bucket) => ({
      label: bucket.label,
      tasks: bucket.tasks,
    }));
    this.copyTaskSectionsToClipboard('Sprint planning report', sections, 'brief');
  }

  protected copyTaggedReport(): void {
    const tag = this.selectedReportTag();
    if (!tag) {
      return;
    }
    this.copyTaskSectionsToClipboard(`Tag report: ${tag}`, [{ label: tag, tasks: this.taggedReportTasks() }], 'brief');
  }

  protected copyCurrentWorkDetailed(): void {
    this.copyTaskSectionsToClipboard('Current work report', this.currentWorkReport().map((bucket) => ({ label: bucket.name, tasks: bucket.tasks })), 'detailed');
  }

  protected copyPlannedTaskDetailed(): void {
    this.copyTaskSectionsToClipboard('Planned tasks report', this.plannedTaskBuckets().map((bucket) => ({ label: bucket.label, tasks: bucket.tasks })), 'detailed');
  }

  protected copySprintPlanDetailed(): void {
    this.copyTaskSectionsToClipboard('Sprint planning report', this.sprintPlans().map((bucket) => ({ label: bucket.label, tasks: bucket.tasks })), 'detailed');
  }

  protected copyTaggedReportDetailed(): void {
    const tag = this.selectedReportTag();
    if (!tag) {
      return;
    }
    this.copyTaskSectionsToClipboard(`Tag report: ${tag}`, [{ label: tag, tasks: this.taggedReportTasks() }], 'detailed');
  }

  protected exportCurrentWorkCsv(): void {
    this.exportTaskSectionsCsv('current-work-report', this.currentWorkReport().map((bucket) => ({ label: bucket.name, tasks: bucket.tasks })));
  }

  protected exportPlannedTasksCsv(): void {
    this.exportTaskSectionsCsv('planned-tasks-report', this.plannedTaskBuckets().map((bucket) => ({ label: bucket.label, tasks: bucket.tasks })));
  }

  protected exportSprintPlanCsv(): void {
    this.exportTaskSectionsCsv('sprint-plan-report', this.sprintPlans().map((bucket) => ({ label: bucket.label, tasks: bucket.tasks })));
  }

  protected exportTaggedReportCsv(): void {
    const tag = this.selectedReportTag();
    if (!tag) {
      return;
    }
    this.exportTaskSectionsCsv(`tag-report-${tag}`, [{ label: tag, tasks: this.taggedReportTasks() }]);
  }

  protected printReportSummaryView(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const summaryCards = this.reportSummaryCards()
      .map((card) => `<li><strong>${this.escapeHtml(String(card.label))}:</strong> ${this.escapeHtml(String(card.value))} <span>${this.escapeHtml(card.hint)}</span></li>`)
      .join('');
    const sections = [
      ...this.currentWorkReport().map((bucket) => ({ label: bucket.name, tasks: bucket.tasks })),
      ...this.plannedTaskBuckets().map((bucket) => ({ label: bucket.label, tasks: bucket.tasks })),
    ];
    const sectionMarkup = sections
      .filter((section) => section.tasks.length)
      .map((section) => `
        <section>
          <h2>${this.escapeHtml(section.label)}</h2>
          <ul>
            ${section.tasks.map((task) => `<li><strong>${this.escapeHtml(task.title)}</strong><br>${this.escapeHtml(this.summarizeDescription(task.description) || 'No description')}</li>`).join('')}
          </ul>
        </section>
      `)
      .join('');

    const markup = `
      <html>
        <head>
          <title>Task report summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
            h1, h2 { margin: 0 0 12px; }
            ul { padding-left: 18px; }
            li { margin-bottom: 10px; }
            section { margin-top: 24px; }
            span { color: #6b7280; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>${this.escapeHtml(this.formatProjectTitle(this.getSelectedProject()?.name))} task report</h1>
          <ul>${summaryCards}</ul>
          ${sectionMarkup}
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 300);
    };

    const printFrameWindow = iframe.contentWindow;
    if (!printFrameWindow) {
      iframe.remove();
      this.snackbar.showNotification('error', '', 'Unable to prepare print preview.', 'Close');
      return;
    }

    try {
      printFrameWindow.document.open();
      printFrameWindow.document.write(markup);
      printFrameWindow.document.close();
    } catch {
      iframe.remove();
      this.snackbar.showNotification('error', '', 'Unable to prepare print preview.', 'Close');
      return;
    }

    const triggerPrint = () => {
      try {
        printFrameWindow.focus();
        if (typeof printFrameWindow.print === 'function') {
          printFrameWindow.print();
        } else {
          throw new Error('Print is not available');
        }
      } catch {
        this.snackbar.showNotification('error', '', 'Printing is not available in this browser window.', 'Close');
      } finally {
        cleanup();
      }
    };

    if (printFrameWindow.document.readyState === 'complete') {
      setTimeout(triggerPrint, 50);
      return;
    }

    iframe.addEventListener('load', () => setTimeout(triggerPrint, 50), { once: true });
  }

  protected toggleTaskSelection(taskId: string, checked: boolean): void {
    this.selectedTaskIds.update((ids) => checked
      ? (ids.includes(taskId) ? ids : [...ids, taskId])
      : ids.filter((id) => id !== taskId));
  }

  protected isTaskSelected(taskId: string): boolean {
    return this.selectedTaskIds().includes(taskId);
  }

  protected clearTaskSelection(): void {
    this.selectedTaskIds.set([]);
  }

  protected markNotificationRead(notificationId: string): void {
    this.store.dispatch(TaskActions.TaskNotificationMarkReadRequest({ notificationId }));
  }

  protected markAllNotificationsRead(): void {
    this.store.dispatch(TaskActions.TaskNotificationsMarkAllReadRequest());
  }

  protected clearTaskNotifications(): void {
    this.store.dispatch(TaskActions.TaskNotificationsClearRequest());
  }

  protected openTaskFromNotification(notification: TaskNotification): void {
    this.markNotificationRead(notification.id);
    if (notification.project_id && notification.project_id !== this.selectedProjectId()) {
      this.onProjectChange(notification.project_id);
    }
    const task = this.tasks().find((item) => item.id === notification.task_id);
    if (task) {
      this.openTaskDetails(task);
      return;
    }
    this.selectedTaskId.set(notification.task_id);
    this.store.dispatch(TaskActions.TaskDetailFetchRequest({ taskId: notification.task_id }));
  }

  protected openSaveFilterDialog(): void {
    if (!this.canSaveCurrentFilter()) {
      return;
    }

    const ref = this.dialog.open(SaveFilterDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
    });

    ref.afterClosed().pipe(take(1)).subscribe((result?: { name: string; favorite: boolean; isDefault: boolean }) => {
      if (!result) {
        return;
      }

      const name = result.name.trim();
      if (!name) {
        return;
      }

      const currentUserId = this.currentUser()?.id ?? '';
      const currentProjectId = this.selectedProjectId();
      const nextFilterRecord: TaskSavedFilter = {
        id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`,
        name,
        scope: this.selectedScope(),
        sort: this.selectedSort(),
        searchTerm: this.searchTerm().trim(),
        selectedTags: this.selectedTagFilters(),
        selectedAssigneeIds: this.selectedAssigneeFilters(),
        userId: currentUserId,
        projectId: currentProjectId,
        isFavorite: !!result.favorite,
        isDefault: !!result.isDefault,
      };
      let finalFilters = [
        nextFilterRecord,
        ...this.savedFilters().filter((filter) =>
          !(filter.userId === currentUserId &&
            filter.projectId === currentProjectId &&
            filter.name.toLowerCase() === name.toLowerCase()),
        ),
      ];
      if (nextFilterRecord.isDefault) {
        finalFilters = finalFilters.map((filter) =>
          filter.userId === currentUserId && filter.id !== nextFilterRecord.id
            ? { ...filter, isDefault: false }
            : filter,
        );
      }
      this.savedFilters.set(finalFilters);
      this.persistSavedFilters();
      this.snackbar.showNotification('success', '', 'Saved view added.', 'Close');
    });
  }

  protected applySavedFilter(filter: TaskSavedFilter): void {
    this.selectedScope.set(filter.scope);
    this.selectedSort.set(filter.sort);
    this.searchTerm.set(filter.searchTerm);
    this.selectedTagFilters.set(filter.selectedTags ?? []);
    this.selectedAssigneeFilters.set(filter.selectedAssigneeIds ?? []);
    this.showSearch.set(!!filter.searchTerm);
  }

  protected deleteSavedFilter(filter: TaskSavedFilter): void {
    const nextFilters = this.savedFilters().filter((item) => item.id !== filter.id);
    this.savedFilters.set(nextFilters);
    this.persistSavedFilters();
  }

  protected toggleSavedFilterFavorite(filter: TaskSavedFilter): void {
    this.savedFilters.update((filters) => filters.map((item) =>
      item.id === filter.id ? { ...item, isFavorite: !item.isFavorite } : item,
    ));
    this.persistSavedFilters();
  }

  protected setDefaultSavedFilter(filter: TaskSavedFilter): void {
    this.savedFilters.update((filters) => filters.map((item) => {
      if (item.userId !== filter.userId) {
        return item;
      }
      if (item.id === filter.id) {
        return { ...item, isDefault: true };
      }
      return item.isDefault ? { ...item, isDefault: false } : item;
    }));
    this.persistSavedFilters();
  }

  protected isSavedFilterActive(filter: TaskSavedFilter): boolean {
    return this.selectedScope() === filter.scope &&
      this.selectedSort() === filter.sort &&
      this.searchTerm().trim() === filter.searchTerm &&
      this.areStringArraysEqual(this.selectedTagFilters(), filter.selectedTags ?? []) &&
      this.areStringArraysEqual(this.selectedAssigneeFilters(), filter.selectedAssigneeIds ?? []);
  }

  protected isDefaultSavedFilter(filter: TaskSavedFilter): boolean {
    return !!filter.isDefault;
  }

  protected onProjectChange(projectId: string): void {
    this.selectedProjectId.set(projectId || null);
    this.selectedSprintFilter.set(ALL_SPRINTS_FILTER);
    this.closeTaskDetails();
    this.clearTaskSelection();
    if (projectId) {
      this.store.dispatch(TaskActions.TaskSprintsFetchRequest({ projectId }));
      this.store.dispatch(TaskActions.TaskItemsFetchRequest({ projectId }));
      queueMicrotask(() => this.applyDefaultSavedFilter());
    } else {
      this.sprints.set([]);
      this.tasks.set([]);
    }
  }

  protected projectTasks(status?: string): TaskItem[] {
    const tasks = this.filteredTasks();
    return status ? tasks.filter((task) => task.status === status) : tasks;
  }

  protected buildTaskPayload(task: TaskItem, overrides: Partial<TaskUpsertPayload> = {}): TaskUpsertPayload {
    return {
      project_id: task.project_id,
      sprint_id: task.sprint_id ?? '',
      title: task.title,
      description: task.description ?? '',
      status: task.status,
      priority: task.priority,
      start_at: task.start_at ?? '',
      due_at: task.due_at ?? '',
      estimated_minutes: task.estimated_minutes ?? 0,
      tracked_minutes: task.tracked_minutes ?? 0,
      tags: task.tags ?? [],
      ...overrides,
    };
  }

  protected dropListId(status: string): string {
    return `tasks-status-${status}`;
  }

  protected onTaskDrop(event: CdkDragDrop<TaskItem[]>, nextStatus: string): void {
    const task = event.item.data as TaskItem | undefined;
    if (!task || task.status === nextStatus || !this.canEditTask(task)) {
      return;
    }

    this.requestTaskStatusChange(task, nextStatus, { silentSuccess: true, optimisticUi: true });
  }

  protected getSelectedProject(): TaskProject | null {
    const projectId = this.selectedProjectId();
    return this.projects().find((project) => project.id === projectId) ?? null;
  }

  protected openColumnDialog(): void {
    const project = this.getSelectedProject();
    if (!project || !this.canManageProject()) {
      return;
    }

    const ref = this.dialog.open(ColumnDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        statuses: this.statuses(),
      },
    });

    ref.afterClosed().subscribe((result?: TaskColumn[]) => {
      if (!result?.length) {
        return;
      }

      this.store.dispatch(TaskActions.TaskProjectUpdateRequest({
        projectId: project.id,
        payload: {
          name: project.name,
          description: project.description ?? '',
          statuses: result,
          member_user_ids: project.member_user_ids ?? [],
        },
        successMessage: 'Column added.',
      }));
    });
  }

  protected openRenameColumnDialog(column: TaskColumn): void {
    const project = this.getSelectedProject();
    if (!project || !this.canManageProject()) {
      return;
    }

    const ref = this.dialog.open(ColumnManageDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        column,
      },
    });

    ref.afterClosed().subscribe((result?: { label: string }) => {
      if (!result) {
        return;
      }

      const nextLabel = result.label.trim();
      if (!nextLabel) {
        return;
      }
      const nextStatuses = this.statuses().map((status) => status.key === column.key ? { ...status, label: nextLabel } : status);
      this.saveProjectColumns(project, nextStatuses, 'Column updated.');
    });
  }

  protected deleteColumn(column: TaskColumn): void {
    const project = this.getSelectedProject();
    if (!project || !this.canManageProject()) {
      return;
    }
    if (this.projectTasks(column.key).length) {
      this.snackbar.showNotification('error', '', 'Move tasks out of this column before deleting it.', 'Close');
      return;
    }
    const nextStatuses = this.statuses().filter((status) => status.key !== column.key);
    if (!nextStatuses.length) {
      this.snackbar.showNotification('error', '', 'At least one column is required.', 'Close');
      return;
    }

    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Delete Column',
        message: `Delete "${column.label}"? This will remove the column from the project board.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.saveProjectColumns(project, nextStatuses, 'Column deleted.');
    });
  }

  protected onColumnDrop(event: CdkDragDrop<TaskColumn[]>): void {
    const project = this.getSelectedProject();
    if (!project || !this.canManageProject() || event.previousIndex === event.currentIndex) {
      return;
    }
    const nextStatuses = [...this.statuses()];
    moveItemInArray(nextStatuses, event.previousIndex, event.currentIndex);
    this.projects.update((projects) =>
      projects.map((item) =>
        item.id === project.id ? { ...item, statuses: nextStatuses } : item,
      ),
    );
    this.saveProjectColumns(project, nextStatuses, 'Board updated.', true);
  }

  protected openProjectDialog(project?: TaskProject): void {
    const ref = this.dialog.open(ProjectDialogComponent, {
      width: '460px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        title: project ? 'Edit Project' : 'New Project',
        project,
        organizationUsers: this.organizationUsers(),
      },
    });

    ref.afterClosed().subscribe((result?: { name: string; description: string; member_user_ids: string[] }) => {
      if (!result) {
        return;
      }

      if (project) {
        this.store.dispatch(TaskActions.TaskProjectUpdateRequest({
          projectId: project.id,
          payload: { ...result, statuses: project.statuses?.length ? project.statuses : [...TASK_STATUSES] },
          successMessage: 'Project updated.',
        }));
        return;
      }

      this.store.dispatch(TaskActions.TaskProjectCreateRequest({
        payload: { ...result, statuses: [...TASK_STATUSES] },
        successMessage: 'Project created.',
      }));
    });
  }

  protected openSprintDialog(sprint?: TaskSprint): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.canManageProject()) {
      return;
    }

    const ref = this.dialog.open(SprintDialogComponent, {
      width: '460px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        title: sprint ? 'Edit Sprint' : 'New Sprint',
        sprint,
      },
    });

    ref.afterClosed().pipe(take(1)).subscribe((result?: { name: string; goal: string; start_date: string; end_date: string; is_active: boolean }) => {
      if (!result) {
        return;
      }

      if (sprint) {
        this.store.dispatch(TaskActions.TaskSprintUpdateRequest({
          projectId,
          sprintId: sprint.id,
          payload: result,
          successMessage: 'Sprint updated.',
        }));
        return;
      }

      this.store.dispatch(TaskActions.TaskSprintCreateRequest({
        projectId,
        payload: result,
        successMessage: 'Sprint created.',
      }));
    });
  }

  protected archiveProject(project: TaskProject): void {
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Archive Project',
        message: `Archive "${project.name}"? You can add restore later, but it will be hidden from the active project list now.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(TaskActions.TaskProjectUpdateRequest({
        projectId: project.id,
        payload: {
          name: project.name,
          description: project.description ?? '',
          statuses: project.statuses?.length ? project.statuses : [...TASK_STATUSES],
          member_user_ids: project.member_user_ids ?? [],
          is_archived: true,
        },
        successMessage: 'Project archived.',
      }));
    });
  }

  protected openTaskDialog(task?: TaskItem, suggestedStatus?: string): void {
    const ref = this.dialog.open(TaskDialogComponent, {
      width: '560px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        title: task ? 'Edit Task' : 'New Task',
        task,
        projectId: this.selectedProjectId(),
        sprints: this.sprints(),
        organizationUsers: this.getProjectUsers(),
        savedTags: this.availableTaskTags(),
        statuses: this.statuses(),
        priorities: this.priorities,
        suggestedStatus,
      },
    });

    ref.afterClosed().subscribe((result?: Record<string, unknown>) => {
      if (!result) {
        return;
      }

      if (task) {
        this.requestTaskUpdate(task, result as TaskUpsertPayload, { successMessage: 'Task updated.' });
        return;
      }

      this.store.dispatch(TaskActions.TaskCreateRequest({
        payload: result as TaskUpsertPayload,
        successMessage: 'Task created.',
      }));
    });
  }

  protected openTaskDetails(task: TaskItem): void {
    this.selectedTaskId.set(task.id);
    this.selectedTask.set(task);
    this.store.dispatch(TaskActions.TaskDetailFetchRequest({ taskId: task.id }));
  }

  protected openTaskDetailsById(taskId: string): void {
    const task = this.tasks().find((item) => item.id === taskId);
    if (!task) {
      return;
    }
    this.openTaskDetails(task);
  }

  protected closeMenu(menu?: { closeMenu: () => void } | null): void {
    menu?.closeMenu();
  }

  protected closeTaskDetails(): void {
    this.selectedTaskId.set(null);
    this.selectedTask.set(null);
  }

  protected handleTaskUpdated(task: TaskItem): void {
    this.selectedTask.set(task);
    this.tasks.update((tasks) => tasks.map((item) => item.id === task.id ? { ...item, ...task } : item));
  }

  protected archiveTask(task: TaskItem): void {
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Archive Task',
        message: `Archive "${task.title}"? It will be removed from the active task views.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(TaskActions.TaskUpdateRequest({
        taskId: task.id,
        payload: this.buildTaskPayload(task, { is_archived: true }),
        successMessage: 'Task archived.',
      }));
    });
  }

  protected bulkMoveTasks(status: string): void {
    const tasks = this.selectedTasks().filter((task) => this.canEditTask(task) && task.status !== status);
    if (!tasks.length) {
      return;
    }

    if (status !== 'done') {
      for (const task of tasks) {
        this.applyTaskStatusUpdate(task, status, true);
      }
      this.clearTaskSelection();
      return;
    }

    const readyTasks = tasks.filter((task) => !this.hasIncompleteSubtasks(task));
    const guardedTasks = tasks.filter((task) => this.hasIncompleteSubtasks(task));

    for (const task of readyTasks) {
      this.applyTaskStatusUpdate(task, status, true);
    }

    if (!guardedTasks.length) {
      this.clearTaskSelection();
      return;
    }

    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Close Tasks With Open Subtasks?',
        message: `${guardedTasks.length} selected task${guardedTasks.length === 1 ? '' : 's'} still ${guardedTasks.length === 1 ? 'has' : 'have'} incomplete subtasks. Mark ${guardedTasks.length === 1 ? 'it' : 'them'} as done anyway?`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (confirmed) {
        for (const task of guardedTasks) {
          this.applyTaskStatusUpdate(task, status, true);
        }
      }
      this.clearTaskSelection();
    });
  }

  protected bulkArchiveTasks(): void {
    const tasks = this.selectedTasks().filter((task) => this.canEditTask(task));
    if (!tasks.length) {
      return;
    }
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Archive Tasks',
        message: `Archive ${tasks.length} selected task${tasks.length === 1 ? '' : 's'}? They will be removed from the active task views.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      for (const task of tasks) {
        this.store.dispatch(TaskActions.TaskUpdateRequest({
          taskId: task.id,
          payload: this.buildTaskPayload(task, { is_archived: true }),
          silentSuccess: true,
        }));
      }
      this.clearTaskSelection();
    });
  }

  protected canManageProject(): boolean {
    const role = this.currentUser()?.role?.trim().toLowerCase() ?? '';
    return role === 'owner' || role === 'admin';
  }

  protected canCreateProject(): boolean {
    return this.canManageProject();
  }

  protected canEditTask(task: TaskItem): boolean {
    const currentUser = this.currentUser();
    if (!currentUser) {
      return false;
    }
    const role = currentUser.role?.trim().toLowerCase() ?? '';
    if (role === 'owner' || role === 'admin') {
      return true;
    }
    return this.hasAssignedSubtask(task, currentUser.id);
  }

  protected hasAssignedSubtask(task: TaskItem | null | undefined, userId: string | null | undefined): boolean {
    const normalizedUserId = userId?.trim();
    if (!task || !normalizedUserId) {
      return false;
    }

    return (task.subtasks ?? []).some((subtask) => (subtask.assignee_user_id ?? '') === normalizedUserId);
  }

  protected getUserName(userId: string | null | undefined): string {
    if (!userId) {
      return 'Unassigned';
    }
    return this.organizationUsers().find((user) => user.id === userId)?.name?.trim() || 'Unassigned';
  }

  protected getIncompleteSubtaskCount(task: TaskItem | null | undefined): number {
    return (task?.subtasks ?? []).filter((subtask) => !subtask.is_completed).length;
  }

  protected hasIncompleteSubtasks(task: TaskItem | null | undefined): boolean {
    return this.getIncompleteSubtaskCount(task) > 0;
  }

  protected getUserInitials(userId: string | null | undefined): string {
    return this.getInitials(this.getUserName(userId), 'U');
  }

  protected getUserEmail(userId: string | null | undefined): string | null {
    if (!userId) {
      return null;
    }

    return this.organizationUsers().find((user) => user.id === userId)?.email?.trim() || null;
  }

  protected getUserAvatarUrl(userId: string | null | undefined): string | null {
    if (!userId) {
      return null;
    }

    const user = this.organizationUsers().find((candidate) => candidate.id === userId);
    const url = normalizeUserAttributes(user?.attributes ?? null)['profile_photo_url'];
    return this.toRenderablePhotoUrl(url);
  }

  protected getTaskAssigneeIds(task: TaskItem | null | undefined): string[] {
    if (!task) {
      return [];
    }
    const ids = (task.subtasks ?? [])
      .map((subtask) => subtask.assignee_user_id ?? '')
      .filter((id) => !!id);
    return [...new Set(ids)];
  }

  protected getPrimaryTaskAssigneeId(task: TaskItem | null | undefined): string | null {
    return this.getTaskAssigneeIds(task)[0] ?? null;
  }

  protected getVisibleTaskAssigneeIds(task: TaskItem | null | undefined, limit: number = 3): string[] {
    return this.getTaskAssigneeIds(task).slice(0, limit);
  }

  protected getTaskAssigneeOverflowCount(task: TaskItem | null | undefined, limit: number = 3): number {
    return Math.max(this.getTaskAssigneeIds(task).length - limit, 0);
  }

  private toRenderablePhotoUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^(https?:|blob:|data:)/i.test(trimmed)) {
      if (/^(blob:|data:)/i.test(trimmed)) {
        return trimmed;
      }

      try {
        const candidateUrl = new URL(trimmed);
        const apiBaseUrl = new URL(environment.apiBaseUrl);
        if (candidateUrl.origin === apiBaseUrl.origin) {
          return trimmed;
        }
      } catch {
        return null;
      }

      return null;
    }

    if (trimmed.startsWith('/')) {
      return new URL(trimmed, environment.apiBaseUrl).toString();
    }

    return null;
  }

  protected getTaskAssigneeNames(task: TaskItem | null | undefined): string {
    const names = this.getTaskAssigneeIds(task)
      .map((userId) => this.getUserName(userId))
      .filter((name) => name !== 'Unassigned');
    if (!names.length) {
      return 'Unassigned';
    }
    return names.join(', ');
  }

  protected getTaskAssigneeSummary(task: TaskItem | null | undefined): string {
    const names = this.getTaskAssigneeIds(task)
      .map((userId) => this.getUserName(userId))
      .filter((name) => name !== 'Unassigned');
    if (!names.length) {
      return 'Unassigned';
    }
    if (names.length === 1) {
      return names[0];
    }
    return `${names[0]} +${names.length - 1}`;
  }

  protected hasTaskAssignee(task: TaskItem | null | undefined, userId: string | null | undefined): boolean {
    if (!userId) {
      return false;
    }
    return this.getTaskAssigneeIds(task).includes(userId);
  }

  protected getProjectUsers(project: TaskProject | null = this.getSelectedProject()): User[] {
    const users = this.organizationUsers();
    const memberIDs = project?.member_user_ids ?? [];
    if (!memberIDs.length) {
      return users;
    }
    return users.filter((user) => memberIDs.includes(user.id));
  }

  protected formatDueDate(value: string | null | undefined): string {
    if (!value) {
      return 'No due date';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'No due date';
    }
    return parsed.toLocaleString();
  }

  protected formatDateOnly(value: string | null | undefined): string {
    if (!value) {
      return 'No date';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'No date';
    }
    return parsed.toLocaleDateString();
  }

  protected formatNotificationTime(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleString();
  }

  protected getDueState(value: string | null | undefined): 'none' | 'today' | 'upcoming' | 'overdue' {
    if (!value) {
      return 'none';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'none';
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfTomorrow = startOfToday + (24 * 60 * 60 * 1000);
    const dueTime = parsed.getTime();

    if (dueTime < startOfToday) {
      return 'overdue';
    }
    if (dueTime < startOfTomorrow) {
      return 'today';
    }
    return 'upcoming';
  }

  protected getDueBadgeLabel(value: string | null | undefined): string {
    switch (this.getDueState(value)) {
      case 'overdue':
        return 'Overdue';
      case 'today':
        return 'Due today';
      case 'upcoming':
        return 'Upcoming';
      default:
        return 'No due date';
    }
  }

  protected formatTags(tags: string[] | undefined): string {
    return (tags ?? []).join(', ');
  }

  protected formatMinutes(value: number | null | undefined): string {
    const minutes = Number(value ?? 0);
    if (!minutes || minutes < 0) {
      return '0m';
    }
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  }

  protected summarizeDescription(value: string | null | undefined): string {
    return summarizeTaskDescription(value);
  }

  protected renderDescription(value: string | null | undefined): string {
    return renderTaskDescription(value);
  }

  protected formatStatus(status: string): string {
    return this.statuses().find((column) => column.key === status)?.label ?? status.replaceAll('_', ' ');
  }

  protected getSprintName(sprintId: string | null | undefined): string {
    if (!sprintId) {
      return 'No sprint';
    }
    return this.sprints().find((sprint) => sprint.id === sprintId)?.name ?? 'Unknown sprint';
  }

  protected isSprintFilterSelected(value: string): boolean {
    return this.selectedSprintFilter() === value;
  }

  protected getSprintByName(name: string): TaskSprint | null {
    return this.sprints().find((sprint) => sprint.name === name) ?? null;
  }

  protected getTagTaskCount(tag: string): number {
    return this.reportTasks().filter((task) => (task.tags ?? []).includes(tag)).length;
  }

  protected getTagOverdueCount(tag: string): number {
    return this.reportTasks().filter((task) =>
      (task.tags ?? []).includes(tag) &&
      this.getDueState(task.due_at) === 'overdue' &&
      task.status !== 'done',
    ).length;
  }

  protected getTagInProgressCount(tag: string): number {
    return this.reportTasks().filter((task) =>
      (task.tags ?? []).includes(tag) &&
      task.status === 'in_progress',
    ).length;
  }

  protected getScopeLabel(scope: TaskScope): string {
    return this.scopeOptions.find((option) => option.value === scope)?.label ?? 'All tasks';
  }

  protected getSortLabel(sort: TaskSort): string {
    return this.sortOptions.find((option) => option.value === sort)?.label ?? 'Recently updated';
  }

  protected formatAssigneeFilterNames(userIds: string[] | null | undefined): string {
    const names = (userIds ?? [])
      .map((userId) => this.getUserName(userId))
      .filter((name) => name !== 'Unassigned');

    return names.length ? names.join(', ') : 'Unassigned';
  }

  protected formatSavedFilterContext(filter: TaskSavedFilter): string {
    const detailParts: string[] = [];
    if (filter.selectedTags?.length) {
      detailParts.push(`${filter.selectedTags.length} tag${filter.selectedTags.length === 1 ? '' : 's'}`);
    }
    if (filter.selectedAssigneeIds?.length) {
      detailParts.push(`${filter.selectedAssigneeIds.length} assignee${filter.selectedAssigneeIds.length === 1 ? '' : 's'}`);
    }

    if (!filter.projectId) {
      return detailParts.length ? `Org view · ${detailParts.join(' · ')}` : 'Org view';
    }
    const projectName = this.projects().find((project) => project.id === filter.projectId)?.name ?? 'Project view';
    return detailParts.length ? `${projectName} · ${detailParts.join(' · ')}` : projectName;
  }

  protected formatProjectTitle(value: string | null | undefined): string {
    const text = (value ?? '').trim();
    if (!text) {
      return 'Select a project';
    }
    return text
      .split(/\s+/)
      .map((part) => part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part)
      .join(' ');
  }

  protected shouldShowProjectDescription(): boolean {
    const project = this.getSelectedProject();
    if (!project) {
      return false;
    }
    const name = (project.name ?? '').trim().toLowerCase();
    const description = (project.description ?? '').trim();
    if (!description) {
      return false;
    }
    return description.toLowerCase() !== name;
  }

  protected trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  protected priorityRank(priority: TaskPriority): number {
    switch (priority) {
      case 'urgent':
        return 4;
      case 'high':
        return 3;
      case 'medium':
        return 2;
      default:
        return 1;
    }
  }

  protected getCompletedSubtaskCount(task: TaskItem): number {
    return (task.subtasks ?? []).filter((subtask) => subtask.is_completed).length;
  }

  protected hasRunningTimer(task: TaskItem): boolean {
    return (task.time_entries ?? []).some((entry) => !entry.ended_at);
  }

  protected getTimelineFlex(minutes: number): number {
    return Math.max(minutes || 0, 20);
  }

  private dateSortValue(value: string | null | undefined): number {
    if (!value) {
      return Number.MAX_SAFE_INTEGER;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
  }

  private isWithinDays(value: string | null | undefined, days: number): boolean {
    if (!value) {
      return false;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return false;
    }
    const now = new Date();
    const ceiling = new Date(now);
    ceiling.setDate(ceiling.getDate() + days);
    return parsed >= now && parsed <= ceiling;
  }

  private copyTaskSectionsToClipboard(title: string, sections: Array<{ label: string; tasks: TaskItem[] }>, format: ReportCopyFormat): void {
    const normalizedSections = sections
      .map((section) => ({
        label: section.label,
        tasks: section.tasks.filter((task) => !task.is_archived),
      }))
      .filter((section) => section.tasks.length > 0);

    if (!normalizedSections.length) {
      this.snackbar.showNotification('error', '', 'There are no tasks to copy in this report.', 'Close');
      return;
    }

    const content = [
      title,
      '',
      ...normalizedSections.flatMap((section) => [
        `${section.label}`,
        ...section.tasks.map((task, index) => {
          const description = this.summarizeDescription(task.description) || 'No description';
          if (format === 'brief') {
            return `${index + 1}. ${task.title}\nDescription: ${description}`;
          }
          return [
            `${index + 1}. ${task.title}`,
            `Description: ${description}`,
            `Status: ${this.formatStatus(task.status)}`,
            `Sprint: ${this.getSprintName(task.sprint_id)}`,
            `Subtask owners: ${this.getTaskAssigneeNames(task)}`,
            `Due: ${this.getDueBadgeLabel(task.due_at)}`,
            `Tags: ${(task.tags ?? []).join(', ') || 'None'}`,
            `Estimate: ${this.formatMinutes(task.estimated_minutes)}`,
            `Tracked: ${this.formatMinutes(task.tracked_minutes)}`,
          ].join('\n');
        }),
        '',
      ]),
    ].join('\n');

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(content)
        .then(() => this.snackbar.showNotification('success', '', 'Report copied to clipboard.', 'Close'))
        .catch(() => this.fallbackCopy(content));
      return;
    }

    this.fallbackCopy(content);
  }

  private fallbackCopy(content: string): void {
    if (typeof document === 'undefined') {
      this.snackbar.showNotification('error', '', 'Clipboard is not available here.', 'Close');
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);
    this.snackbar.showNotification(successful ? 'success' : 'error', '', successful ? 'Report copied to clipboard.' : 'Unable to copy report.', 'Close');
  }

  private exportTaskSectionsCsv(filenamePrefix: string, sections: Array<{ label: string; tasks: TaskItem[] }>): void {
    const rows = [['Section', 'Title', 'Description', 'Status', 'Sprint', 'Subtask Owners', 'Due', 'Priority', 'Tags', 'Estimate Minutes', 'Tracked Minutes']];
    for (const section of sections) {
      for (const task of section.tasks.filter((item) => !item.is_archived)) {
        rows.push([
          section.label,
          task.title,
          this.summarizeDescription(task.description) || '',
          this.formatStatus(task.status),
          this.getSprintName(task.sprint_id),
          this.getTaskAssigneeNames(task),
          this.formatDueDate(task.due_at),
          task.priority,
          (task.tags ?? []).join(', '),
          String(task.estimated_minutes ?? 0),
          String(task.tracked_minutes ?? 0),
        ]);
      }
    }

    if (rows.length === 1 || typeof document === 'undefined') {
      this.snackbar.showNotification('error', '', 'There are no tasks to export.', 'Close');
      return;
    }

    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filenamePrefix}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  private getTrackedMinutesForRange(task: TaskItem, rangeStart: Date | null, rangeEnd: Date | null, subtaskId?: string | null, userId?: string | null): number {
    const normalizedUserId = (userId ?? '').trim();
    const normalizedSubtaskId = (subtaskId ?? '').trim();
    return (task.time_entries ?? []).reduce((sum, entry) => {
      if (normalizedSubtaskId) {
        if ((entry.subtask_id ?? '') !== normalizedSubtaskId) {
          return sum;
        }
      } else if (!entry.subtask_id) {
        // keep entries with no subtask linkage in the fallback bucket
      } else {
        return sum;
      }

      if (normalizedUserId && (entry.created_by ?? '') !== normalizedUserId) {
        return sum;
      }

      const startedAt = entry.started_at ? new Date(entry.started_at) : null;
      const endedAt = entry.ended_at ? new Date(entry.ended_at) : new Date();
      if (!endedAt || Number.isNaN(endedAt.getTime())) {
        return sum;
      }
      if (rangeStart && endedAt < rangeStart) {
        return sum;
      }
      if (rangeEnd && startedAt && startedAt > rangeEnd) {
        return sum;
      }
      return sum + (entry.minutes ?? 0);
    }, 0);
  }

  private getOpenSubtaskWorkItems(task: TaskItem): Array<{ ownerId: string; subtaskId: string | null }> {
    const subtasks = (task.subtasks ?? []).filter((subtask) => !subtask.is_completed);
    if (!subtasks.length) {
      return [{ ownerId: '', subtaskId: null }];
    }
    return subtasks.map((subtask) => ({
      ownerId: (subtask.assignee_user_id ?? '').trim(),
      subtaskId: subtask.id,
    }));
  }

  private parseDateRangeBoundary(value: string, boundary: 'start' | 'end'): Date | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    if (boundary === 'end') {
      parsed.setHours(23, 59, 59, 999);
    } else {
      parsed.setHours(0, 0, 0, 0);
    }
    return parsed;
  }

  private buildDateRangeLabels(start: Date, end: Date): string[] {
    const labels: string[] = [];
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const ceiling = new Date(end);
    ceiling.setHours(23, 59, 59, 999);
    while (cursor <= ceiling) {
      labels.push(cursor.toISOString().slice(0, 10));
      cursor.setDate(cursor.getDate() + 1);
    }
    return labels;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private saveProjectColumns(project: TaskProject, statuses: TaskColumn[], successMessage: string, silent: boolean = false): void {
    this.store.dispatch(TaskActions.TaskProjectUpdateRequest({
      projectId: project.id,
      payload: {
        name: project.name,
        description: project.description ?? '',
        statuses,
        member_user_ids: project.member_user_ids ?? [],
      },
      successMessage,
      silentSuccess: silent,
    }));
  }

  private getInitials(name: string | null | undefined, fallback: string = '?'): string {
    const words = (name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) {
      return fallback;
    }
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
  }

  private loadSavedFilters(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const raw = window.localStorage.getItem(TASK_SAVED_FILTERS_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as Array<Partial<TaskSavedFilter>>;
      if (Array.isArray(parsed)) {
        const currentUserId = this.currentUser()?.id ?? '';
        this.savedFilters.set(parsed
          .filter((item): item is Partial<TaskSavedFilter> & { id: string; name: string; scope: TaskScope; sort: TaskSort; searchTerm: string } =>
            typeof item?.id === 'string' &&
            typeof item?.name === 'string' &&
            typeof item?.scope === 'string' &&
            typeof item?.sort === 'string' &&
            typeof item?.searchTerm === 'string',
          )
          .map((item) => ({
            id: item.id,
            name: item.name,
            scope: item.scope,
            sort: item.sort,
            searchTerm: item.searchTerm,
            selectedTags: Array.isArray(item.selectedTags) ? item.selectedTags.filter((tag): tag is string => typeof tag === 'string') : [],
            selectedAssigneeIds: Array.isArray(item.selectedAssigneeIds) ? item.selectedAssigneeIds.filter((id): id is string => typeof id === 'string') : [],
            userId: item.userId ?? currentUserId,
            projectId: item.projectId ?? null,
            isFavorite: !!item.isFavorite,
            isDefault: !!item.isDefault,
          })));
      }
    } catch {
      this.savedFilters.set([]);
    }
  }

  private persistSavedFilters(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(TASK_SAVED_FILTERS_KEY, JSON.stringify(this.savedFilters()));
  }

  private applyDefaultSavedFilter(): void {
    const defaultFilter = this.defaultSavedFilter();
    if (defaultFilter) {
      this.applySavedFilter(defaultFilter);
    }
  }

  private areStringArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const normalizedLeft = [...left].sort();
    const normalizedRight = [...right].sort();
    return normalizedLeft.every((value, index) => value === normalizedRight[index]);
  }

  private requestTaskUpdate(task: TaskItem, payload: TaskUpsertPayload, options: { successMessage?: string; silentSuccess?: boolean; optimisticUi?: boolean } = {}): void {
    const nextStatus = (payload.status ?? task.status ?? '').trim().toLowerCase();
    if (nextStatus === 'done' && this.hasIncompleteSubtasks(task)) {
      this.dialog.open(ConfirmComponent, {
        width: '420px',
        maxWidth: 'calc(100vw - 24px)',
        data: {
          title: 'Close Task With Open Subtasks?',
          message: `${this.getIncompleteSubtaskCount(task)} subtask${this.getIncompleteSubtaskCount(task) === 1 ? ' is' : 's are'} still incomplete. Mark this task as done anyway?`,
        },
      }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.dispatchTaskUpdate(task, payload, options);
      });
      return;
    }

    this.dispatchTaskUpdate(task, payload, options);
  }

  private requestTaskStatusChange(task: TaskItem, nextStatus: string, options: { successMessage?: string; silentSuccess?: boolean; optimisticUi?: boolean } = {}): void {
    this.requestTaskUpdate(task, this.buildTaskPayload(task, { status: nextStatus }), options);
  }

  private applyTaskStatusUpdate(task: TaskItem, nextStatus: string, silentSuccess: boolean): void {
    this.tasks.update((items) => items.map((item) => item.id === task.id ? { ...item, status: nextStatus } : item));
    if (this.selectedTask()?.id === task.id) {
      this.selectedTask.update((current) => current ? { ...current, status: nextStatus } : current);
    }
    this.store.dispatch(TaskActions.TaskUpdateRequest({
      taskId: task.id,
      payload: this.buildTaskPayload(task, { status: nextStatus }),
      silentSuccess,
    }));
  }

  private dispatchTaskUpdate(task: TaskItem, payload: TaskUpsertPayload, options: { successMessage?: string; silentSuccess?: boolean; optimisticUi?: boolean } = {}): void {
    const nextStatus = (payload.status ?? task.status).trim();
    if (options.optimisticUi && nextStatus !== task.status) {
      this.applyTaskStatusUpdate(task, nextStatus, !!options.silentSuccess);
      return;
    }

    this.store.dispatch(TaskActions.TaskUpdateRequest({
      taskId: task.id,
      payload,
      successMessage: options.successMessage,
      silentSuccess: options.silentSuccess,
    }));
  }
}

@Component({
  selector: 'app-project-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="tasks-dialog-form">
        <mat-form-field appearance="outline">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Description</mat-label>
          <textarea matInput rows="4" formControlName="description"></textarea>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Project members</mat-label>
          <mat-select formControlName="member_user_ids" multiple>
            @for (user of data.organizationUsers; track user.id) {
              <mat-option [value]="user.id">{{ user.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="dialogRef.close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" [disabled]="form.invalid" (click)="submit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`.tasks-dialog-form{display:grid;gap:14px;min-width:min(480px,100%);padding-top:8px;}`],
})
class ProjectDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ProjectDialogComponent>);
  readonly data = inject<{ title: string; project?: TaskProject; organizationUsers: User[] }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    name: [this.data.project?.name ?? '', [Validators.required, Validators.minLength(2)]],
    description: [this.data.project?.description ?? ''],
    member_user_ids: [this.data.project?.member_user_ids ?? []],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close(this.form.getRawValue());
  }
}

@Component({
  selector: 'app-task-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatChipsModule, MatIconModule, MatAutocompleteModule, TaskRichTextEditorComponent],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="tasks-dialog-form">
        <mat-form-field appearance="outline">
          <mat-label>Title</mat-label>
          <input matInput formControlName="title" />
        </mat-form-field>

        <div class="tasks-dialog-editor-field">
          <div class="tasks-dialog-editor-field__label">Description</div>
          <app-task-rich-text-editor formControlName="description" placeholder="Describe the task with formatting, lists, and links"></app-task-rich-text-editor>
        </div>

        <div class="tasks-dialog-grid">
          <mat-form-field appearance="outline">
            <mat-label>Status</mat-label>
            <mat-select formControlName="status">
              @for (status of data.statuses; track status.key) {
                <mat-option [value]="status.key">{{ status.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Priority</mat-label>
            <mat-select formControlName="priority">
              @for (priority of data.priorities; track priority.value) {
                <mat-option [value]="priority.value">{{ priority.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="tasks-dialog-grid">
          <mat-form-field appearance="outline">
            <mat-label>Start date</mat-label>
            <input matInput type="date" formControlName="start_at" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Sprint</mat-label>
            <mat-select formControlName="sprint_id">
              <mat-option value="">No sprint</mat-option>
              @for (sprint of data.sprints; track sprint.id) {
                <mat-option [value]="sprint.id">{{ sprint.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="tasks-dialog-grid">
          <mat-form-field appearance="outline">
            <mat-label>Due date</mat-label>
            <input matInput type="date" formControlName="due_at" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Estimate (minutes)</mat-label>
            <input matInput type="number" min="0" formControlName="estimated_minutes" />
          </mat-form-field>
        </div>

        <div class="tasks-dialog-grid">
          <mat-form-field appearance="outline">
            <mat-label>Tracked (minutes)</mat-label>
            <input matInput type="number" min="0" formControlName="tracked_minutes" />
          </mat-form-field>

          <div></div>
        </div>

        <div class="tasks-dialog-editor-field">
          <div class="tasks-dialog-editor-field__label">Tags</div>
          <mat-chip-set class="tasks-dialog-tags">
            @for (tag of form.controls.tags.value; track tag) {
              <mat-chip-row [removable]="true" (removed)="removeTag(tag)">
                {{ tag }}
                <button matChipRemove type="button" aria-label="Remove tag">
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              </mat-chip-row>
            }
          </mat-chip-set>
          <mat-form-field appearance="outline">
            <mat-label>Add or select tag</mat-label>
            <input
              matInput
              [value]="tagDraft()"
              (input)="tagDraft.set($any($event.target).value)"
              (keydown.enter)="addTagFromInput($event)"
              (keydown.comma)="addTagFromInput($event)"
              (blur)="commitTagDraft()"
              [matAutocomplete]="tagAutocomplete"
              placeholder="Type to add or select a tag"
            />
            <mat-autocomplete #tagAutocomplete="matAutocomplete" autoActiveFirstOption (optionSelected)="onSuggestedTagSelected($event.option.value)">
              @for (tag of suggestedTags(); track tag) {
                <mat-option [value]="tag">{{ tag }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="dialogRef.close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" [disabled]="form.invalid" (click)="submit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .tasks-dialog-form{display:grid;gap:14px;min-width:min(540px,100%);padding-top:8px;}
    .tasks-dialog-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;}
    .tasks-dialog-editor-field{display:grid;gap:8px;}
    .tasks-dialog-editor-field__label{font-size:.8rem;font-weight:700;color:var(--mat-sys-on-surface,#0f172a);}
    .tasks-dialog-tags{margin-bottom:2px;}
    @media (max-width:640px){.tasks-dialog-grid{grid-template-columns:1fr;}}
  `],
})
export class TaskDialogComponent {
  readonly dialogRef = inject(MatDialogRef<TaskDialogComponent>);
  readonly data = inject<{
    title: string;
    task?: TaskItem;
    projectId?: string | null;
    sprints: TaskSprint[];
    organizationUsers: User[];
    savedTags: string[];
    statuses: ReadonlyArray<TaskColumn>;
    priorities: ReadonlyArray<{ value: TaskPriority; label: string }>;
    suggestedStatus?: string;
  }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  readonly tagDraft = signal('');

  readonly form = this.fb.nonNullable.group({
    title: [this.data.task?.title ?? '', [Validators.required, Validators.minLength(2)]],
    description: [this.data.task?.description ?? ''],
    sprint_id: [this.data.task?.sprint_id ?? ''],
    status: [this.data.task?.status ?? this.data.suggestedStatus ?? (this.data.statuses[1]?.key ?? this.data.statuses[0]?.key ?? 'todo')],
    priority: [this.data.task?.priority ?? 'medium'],
    start_at: [this.toInputDate(this.data.task?.start_at ?? null)],
    due_at: [this.toInputDate(this.data.task?.due_at ?? null)],
    estimated_minutes: [this.data.task?.estimated_minutes ?? 0, [Validators.min(0)]],
    tracked_minutes: [this.data.task?.tracked_minutes ?? 0, [Validators.min(0)]],
    tags: [this.data.task?.tags ?? []],
  });
  readonly suggestedTags = computed(() => {
    const selected = new Set(this.form.controls.tags.value);
    const query = this.tagDraft().trim().toLowerCase();

    return (this.data.savedTags ?? [])
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag, index, list) => tag && list.indexOf(tag) === index)
      .filter((tag) => !selected.has(tag))
      .filter((tag) => !query || tag.includes(query))
      .slice(0, 8);
  });

  addTagFromInput(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.commitTagDraft();
  }

  commitTagDraft(): void {
    const nextTag = this.tagDraft().trim().replace(/,+$/g, '');
    if (!nextTag) {
      this.tagDraft.set('');
      return;
    }
    const normalized = nextTag.toLowerCase();
    const existing = this.form.controls.tags.value;
    if (!existing.includes(normalized)) {
      this.form.controls.tags.setValue([...existing, normalized]);
      this.form.controls.tags.markAsDirty();
    }
    this.tagDraft.set('');
  }

  removeTag(tag: string): void {
    this.form.controls.tags.setValue(this.form.controls.tags.value.filter((item) => item !== tag));
    this.form.controls.tags.markAsDirty();
  }

  addSuggestedTag(tag: string): void {
    const normalized = tag.trim().toLowerCase();
    if (!normalized) {
      return;
    }

    const existing = this.form.controls.tags.value;
    if (!existing.includes(normalized)) {
      this.form.controls.tags.setValue([...existing, normalized]);
      this.form.controls.tags.markAsDirty();
    }
    this.tagDraft.set('');
  }

  onSuggestedTagSelected(tag: string | null): void {
    if (!tag) {
      return;
    }
    this.addSuggestedTag(tag);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const payload = {
      project_id: this.data.task?.project_id ?? this.data.projectId ?? '',
      sprint_id: raw.sprint_id,
      title: raw.title.trim(),
      description: raw.description.trim(),
      status: raw.status,
      priority: raw.priority,
      start_at: raw.start_at ? this.toIsoDate(raw.start_at) : '',
      due_at: raw.due_at ? this.toIsoDate(raw.due_at) : '',
      estimated_minutes: Number(raw.estimated_minutes) || 0,
      tracked_minutes: Number(raw.tracked_minutes) || 0,
      tags: raw.tags,
    };

    this.dialogRef.close(payload);
  }

  private toInputDate(value: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
  private toIsoDate(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const date = new Date(`${trimmed}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString();
  }
}

@Component({
  selector: 'app-column-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>Add Column</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="tasks-dialog-form">
        <mat-form-field appearance="outline">
          <mat-label>Column name</mat-label>
          <input matInput formControlName="label" placeholder="Blocked" />
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="dialogRef.close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" [disabled]="form.invalid" (click)="submit()">Add</button>
    </mat-dialog-actions>
  `,
  styles: [`.tasks-dialog-form{display:grid;gap:14px;min-width:min(360px,100%);padding-top:8px;}`],
})
class ColumnDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ColumnDialogComponent>);
  readonly data = inject<{ statuses: ReadonlyArray<TaskColumn> }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    label: ['', [Validators.required, Validators.minLength(2)]],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const label = this.form.getRawValue().label.trim();
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key) {
      return;
    }

    if (this.data.statuses.some((status) => status.key === key)) {
      this.form.controls.label.setErrors({ duplicate: true });
      return;
    }

    this.dialogRef.close([...this.data.statuses, { key, label }]);
  }
}

@Component({
  selector: 'app-column-manage-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>Rename Column</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="tasks-dialog-form">
        <mat-form-field appearance="outline">
          <mat-label>Column name</mat-label>
          <input matInput formControlName="label" />
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="dialogRef.close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" [disabled]="form.invalid" (click)="submit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`.tasks-dialog-form{display:grid;gap:14px;min-width:min(360px,100%);padding-top:8px;}`],
})
class ColumnManageDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ColumnManageDialogComponent>);
  readonly data = inject<{ column: TaskColumn }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    label: [this.data.column.label, [Validators.required, Validators.minLength(2)]],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close(this.form.getRawValue());
  }
}

@Component({
  selector: 'app-sprint-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatCheckboxModule],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="tasks-dialog-form">
        <mat-form-field appearance="outline">
          <mat-label>Sprint name</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Goal</mat-label>
          <textarea matInput rows="3" formControlName="goal"></textarea>
        </mat-form-field>

        <div class="tasks-dialog-grid">
          <mat-form-field appearance="outline">
            <mat-label>Start date</mat-label>
            <input matInput type="datetime-local" formControlName="start_date" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>End date</mat-label>
            <input matInput type="datetime-local" formControlName="end_date" />
          </mat-form-field>
        </div>

        <mat-checkbox formControlName="is_active">Mark as active sprint</mat-checkbox>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="dialogRef.close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" [disabled]="form.invalid" (click)="submit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`.tasks-dialog-form{display:grid;gap:14px;min-width:min(460px,100%);padding-top:8px;}.tasks-dialog-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;}@media (max-width:640px){.tasks-dialog-grid{grid-template-columns:1fr;}}`],
})
class SprintDialogComponent {
  readonly dialogRef = inject(MatDialogRef<SprintDialogComponent>);
  readonly data = inject<{ title: string; sprint?: TaskSprint }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    name: [this.data.sprint?.name ?? '', [Validators.required, Validators.minLength(2)]],
    goal: [this.data.sprint?.goal ?? ''],
    start_date: [this.toInputDate(this.data.sprint?.start_date ?? null)],
    end_date: [this.toInputDate(this.data.sprint?.end_date ?? null)],
    is_active: [this.data.sprint?.is_active ?? false],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.dialogRef.close({
      name: raw.name.trim(),
      goal: raw.goal.trim(),
      start_date: raw.start_date ? new Date(raw.start_date).toISOString() : '',
      end_date: raw.end_date ? new Date(raw.end_date).toISOString() : '',
      is_active: raw.is_active,
    });
  }

  private toInputDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}

@Component({
  selector: 'app-save-filter-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>Save View</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="tasks-dialog-form">
        <mat-form-field appearance="outline">
          <mat-label>View name</mat-label>
          <input matInput formControlName="name" placeholder="My overdue tasks" />
        </mat-form-field>

        <mat-checkbox formControlName="favorite">Pin as favorite</mat-checkbox>
        <mat-checkbox formControlName="isDefault">Use as default view</mat-checkbox>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-button (click)="dialogRef.close()">Cancel</button>
      <button type="button" mat-flat-button color="primary" [disabled]="form.invalid" (click)="submit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`.tasks-dialog-form{display:grid;gap:14px;min-width:min(360px,100%);padding-top:8px;}`],
})
class SaveFilterDialogComponent {
  readonly dialogRef = inject(MatDialogRef<SaveFilterDialogComponent>);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    favorite: [true],
    isDefault: [false],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close(this.form.getRawValue());
  }
}
